import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2, Flashlight, FlashlightOff, Loader2, RotateCcw } from 'lucide-react';
import { QualityIssue, QualityReport, analyzeImage, analyzeRegion, loadImageFromFile } from './documentQuality';
import { prepareUpload } from './documentChecklist';
import { Button, IconButton } from '../UI';

export type FrameShape = 'card' | 'page';

// Proporção largura/altura da moldura: cartão ID-1 (RG/CNH, 85,6 × 54 mm) ou página (A4 / passaporte aberto)
const FRAME_ASPECT: Record<FrameShape, number> = { card: 1.586, page: 0.707 };
const MAX_OUTPUT = 2400;
const ANALYZE_EVERY_MS = 350;

interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface Props {
  title: string;
  frame: FrameShape;
  /** Foto escolhida da galeria: pula a câmera e vai direto para a revisão */
  file?: File | null;
  onConfirm: (file: File) => void;
  onClose: () => void;
  /** Câmera ao vivo indisponível/negada: o chamador abre a câmera nativa */
  onFallback: () => void;
}

const DocumentCamera: React.FC<Props> = ({ title, frame, file, onConfirm, onClose, onFallback }) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const scratchRef = useRef<HTMLCanvasElement | null>(null);
  const recentRef = useRef<QualityIssue[][]>([]);
  const [guide, setGuide] = useState<Rect | null>(null);
  const [ready, setReady] = useState(false);
  const [liveIssues, setLiveIssues] = useState<QualityIssue[] | null>(null);
  const [torchAvailable, setTorchAvailable] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const [review, setReview] = useState<{ file: File; url: string; report: QualityReport } | null>(null);
  const [processing, setProcessing] = useState(false);

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  // Trava a rolagem da página enquanto a câmera está aberta
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  // Foto da galeria → direto para a revisão
  useEffect(() => {
    if (!file) return;
    let cancelled = false;
    (async () => {
      setProcessing(true);
      try {
        const prepared = await prepareUpload(file);
        const img = await loadImageFromFile(prepared);
        if (!cancelled) setReview({ file: prepared, url: URL.createObjectURL(prepared), report: analyzeImage(img) });
      } catch {
        if (!cancelled) onConfirm(file);
      } finally {
        if (!cancelled) setProcessing(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [file]); // eslint-disable-line react-hooks/exhaustive-deps

  // Câmera ao vivo (traseira, na maior resolução que o aparelho oferecer)
  useEffect(() => {
    if (file) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: { facingMode: { ideal: 'environment' }, width: { ideal: 3840 }, height: { ideal: 2160 } },
        });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        const video = videoRef.current;
        if (video) {
          video.srcObject = stream;
          await video.play().catch(() => {});
        }
        const track = stream.getVideoTracks()[0];
        const caps = (track.getCapabilities?.() || {}) as { torch?: boolean };
        setTorchAvailable(!!caps.torch);
      } catch {
        if (!cancelled) onFallback();
      }
    })();
    return () => {
      cancelled = true;
      stopStream();
    };
  }, [file, onFallback, stopStream]);

  // Moldura centralizada, ocupando o máximo possível da tela na proporção do documento
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const compute = () => {
      const cw = el.clientWidth;
      const ch = el.clientHeight;
      const aspect = FRAME_ASPECT[frame];
      const w = Math.min(cw * 0.9, ch * 0.62 * aspect);
      const h = w / aspect;
      setGuide({ x: (cw - w) / 2, y: Math.max(16, (ch - h) / 2 - 32), w, h });
    };
    compute();
    const observer = new ResizeObserver(compute);
    observer.observe(el);
    return () => observer.disconnect();
  }, [frame]);

  /** Converte a moldura (pixels da tela) para pixels do vídeo, considerando o object-cover. */
  const guideInVideo = useCallback((): Rect | null => {
    const video = videoRef.current;
    const el = containerRef.current;
    if (!video || !el || !guide || !video.videoWidth) return null;
    const scale = Math.max(el.clientWidth / video.videoWidth, el.clientHeight / video.videoHeight);
    const ox = (el.clientWidth - video.videoWidth * scale) / 2;
    const oy = (el.clientHeight - video.videoHeight * scale) / 2;
    const x = Math.max(0, (guide.x - ox) / scale);
    const y = Math.max(0, (guide.y - oy) / scale);
    return {
      x,
      y,
      w: Math.min(video.videoWidth - x, guide.w / scale),
      h: Math.min(video.videoHeight - y, guide.h / scale),
    };
  }, [guide]);

  // Dicas ao vivo: analisa só a área da moldura algumas vezes por segundo
  useEffect(() => {
    if (file || review) return undefined;
    const timer = window.setInterval(() => {
      const video = videoRef.current;
      const region = guideInVideo();
      if (!video || !region || video.readyState < 2) return;
      setReady(true);
      if (!scratchRef.current) scratchRef.current = document.createElement('canvas');
      const report = analyzeRegion(video, region.x, region.y, region.w, region.h, scratchRef.current);
      // Suaviza: só mostra um problema se ele apareceu em 2 das últimas 3 leituras
      recentRef.current = [...recentRef.current.slice(-2), report.issues];
      const counts = new Map<QualityIssue, number>();
      recentRef.current.flat().forEach((issue) => counts.set(issue, (counts.get(issue) || 0) + 1));
      setLiveIssues(Array.from(counts.entries()).filter(([, c]) => c >= 2).map(([issue]) => issue));
    }, ANALYZE_EVERY_MS);
    return () => window.clearInterval(timer);
  }, [file, review, guideInVideo]);

  const toggleTorch = async () => {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track) return;
    try {
      await track.applyConstraints({ advanced: [{ torch: !torchOn } as MediaTrackConstraintSet] });
      setTorchOn(!torchOn);
    } catch {
      setTorchAvailable(false);
    }
  };

  const capture = async () => {
    const video = videoRef.current;
    const region = guideInVideo();
    if (!video || !region) return;
    setProcessing(true);
    // Margem de 4% em volta da moldura para não cortar as bordas do documento
    const mx = region.w * 0.04;
    const my = region.h * 0.04;
    const sx = Math.max(0, region.x - mx);
    const sy = Math.max(0, region.y - my);
    const sw = Math.min(video.videoWidth - sx, region.w + 2 * mx);
    const sh = Math.min(video.videoHeight - sy, region.h + 2 * my);
    const scale = Math.min(1, MAX_OUTPUT / Math.max(sw, sh));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(sw * scale);
    canvas.height = Math.round(sh * scale);
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      setProcessing(false);
      return;
    }
    ctx.drawImage(video, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.92));
    setProcessing(false);
    if (!blob) return;
    const captured = new File([blob], `documento-${Date.now()}.jpg`, { type: 'image/jpeg' });
    setReview({ file: captured, url: URL.createObjectURL(captured), report: analyzeImage(canvas) });
  };

  const retake = () => {
    if (review) URL.revokeObjectURL(review.url);
    setReview(null);
    recentRef.current = [];
    setLiveIssues(null);
    if (file) onClose();
  };

  const confirm = () => {
    if (!review) return;
    URL.revokeObjectURL(review.url);
    stopStream();
    onConfirm(review.file);
  };

  const close = () => {
    if (review) URL.revokeObjectURL(review.url);
    stopStream();
    onClose();
  };

  const good = liveIssues !== null && liveIssues.length === 0;
  const hintKey = !ready || liveIssues === null ? 'align' : good ? 'good' : liveIssues[0];

  return createPortal(
    <div className="fixed inset-0 z-[100] bg-black text-white flex flex-col" role="dialog" aria-modal="true" aria-label={title}>
      <div className="flex items-center justify-between gap-3 px-4 pt-[calc(0.75rem+env(safe-area-inset-top))] pb-3">
        <p className="text-sm font-medium truncate">{title}</p>
        <div className="flex items-center gap-2">
          {!review && torchAvailable && (
            <button
              type="button"
              onClick={toggleTorch}
              aria-label={torchOn ? t('documentChecklist.camera.torchOff') : t('documentChecklist.camera.torchOn')}
              className="icon-btn p-2 rounded-full bg-white/10 hover:bg-white/20"
            >
              {torchOn ? <FlashlightOff className="w-5 h-5" /> : <Flashlight className="w-5 h-5" />}
            </button>
          )}
          <IconButton action="close" label={t('documentChecklist.camera.close')} onClick={close} />
        </div>
      </div>

      {review ? (
        <ReviewStage review={review} fromGallery={!!file} onRetake={retake} onConfirm={confirm} />
      ) : (
        <>
          <div ref={containerRef} className="relative flex-1 overflow-hidden">
            {!file && (
              <video ref={videoRef} playsInline muted autoPlay className="absolute inset-0 w-full h-full object-cover" />
            )}
            {guide && !file && (
              <>
                {/* Escurece tudo fora da moldura */}
                <div
                  className={`absolute rounded-xl border-[3px] transition-colors ${good ? 'border-green-400' : 'border-white/90'}`}
                  style={{
                    left: guide.x,
                    top: guide.y,
                    width: guide.w,
                    height: guide.h,
                    boxShadow: '0 0 0 9999px rgba(0,0,0,0.55)',
                  }}
                />
                <div
                  className="absolute left-0 right-0 flex justify-center px-4"
                  style={{ top: guide.y + guide.h + 16 }}
                >
                  <span
                    className={`inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-full ${
                      good ? 'bg-green-500 text-white' : hintKey === 'align' ? 'bg-white/15 text-white' : 'bg-amber-400 text-black'
                    }`}
                  >
                    {good ? <CheckCircle2 className="w-4 h-4" /> : hintKey !== 'align' && <AlertTriangle className="w-4 h-4" />}
                    {t(`documentChecklist.camera.hint.${hintKey}`)}
                  </span>
                </div>
              </>
            )}
            {(processing || (!ready && !file)) && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <Loader2 className="w-8 h-8 animate-spin text-white/80" />
              </div>
            )}
          </div>

          {!file && (
            <div className="px-4 pt-4 pb-[calc(1.25rem+env(safe-area-inset-bottom))] flex flex-col items-center gap-3">
              <p className="text-xs text-white/70 text-center max-w-sm">{t('documentChecklist.camera.tips')}</p>
              <button
                type="button"
                onClick={capture}
                disabled={!ready || processing}
                aria-label={t('documentChecklist.camera.shutter')}
                className={`rounded-full border-4 flex items-center justify-center disabled:opacity-40 ${
                  good ? 'border-green-400' : 'border-white'
                }`}
                style={{ width: 72, height: 72 }}
              >
                <span className={`block w-14 h-14 rounded-full ${good ? 'bg-green-400' : 'bg-white'}`} />
              </button>
            </div>
          )}
        </>
      )}
    </div>,
    document.body
  );
};

const ReviewStage: React.FC<{
  review: { url: string; report: QualityReport };
  fromGallery: boolean;
  onRetake: () => void;
  onConfirm: () => void;
}> = ({ review, fromGallery, onRetake, onConfirm }) => {
  const { t } = useTranslation();
  const issues = review.report.issues;

  return (
    <>
      <div className="relative flex-1 overflow-hidden flex items-center justify-center p-4">
        <img src={review.url} alt="" className="max-w-full max-h-full object-contain rounded-lg" />
      </div>
      <div className="bg-white text-gray-900 rounded-t-2xl px-4 pt-4 pb-[calc(1rem+env(safe-area-inset-bottom))] space-y-3">
        <h2 className="font-semibold">{t('documentChecklist.camera.reviewTitle')}</h2>
        {issues.length === 0 ? (
          <p className="flex items-start gap-2 text-sm text-green-800">
            <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
            {t('documentChecklist.camera.reviewGood')}
          </p>
        ) : (
          <div className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="font-medium mb-1">{t('documentChecklist.camera.reviewIssues')}</p>
            <ul className="list-disc pl-5 space-y-0.5">
              {issues.map((issue) => (
                <li key={issue}>{t(`documentChecklist.camera.issue.${issue}`)}</li>
              ))}
            </ul>
          </div>
        )}
        <p className="text-xs text-gray-600">{t('documentChecklist.camera.checkLegible')}</p>
        <div className="grid grid-cols-2 gap-3">
          <Button variant="ghost"
            type="button"
            onClick={onRetake}>
            <RotateCcw className="w-4 h-4" />
            {fromGallery ? t('documentChecklist.camera.chooseOther') : t('documentChecklist.camera.retake')}
          </Button>
          <button
            type="button"
            onClick={onConfirm}
            className={`py-3 rounded-lg font-medium ${
              issues.length ? 'bg-gray-800 text-white' : 'bg-primary-600 text-white'
            }`}
          >
            {t('documentChecklist.camera.usePhoto')}
          </button>
        </div>
      </div>
    </>
  );
};

export default DocumentCamera;
